import { cardName, type Card } from '@cardengine/shared';
import type { CSSProperties, ReactNode } from 'react';
import { cardArtKey, useDeckArt } from '../cards/deckArt.js';

export interface PlayingCardProps {
  card: Card;
  faceDown?: boolean;
  playable?: boolean;
  selected?: boolean;
  dimmed?: boolean;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

/**
 * A single card face, drawn from Bad Hand Studio's vector deck. The artwork
 * carries its own stock and indices, so the element around it supplies only
 * size, shadow and state — sizing from the `--card-w` custom property on an
 * ancestor, which lets the same component serve the hand, the piles and the
 * seats.
 */
export function PlayingCard({
  card,
  faceDown = false,
  playable = false,
  selected = false,
  dimmed = false,
  style,
  className = '',
  children,
}: PlayingCardProps) {
  const deck = useDeckArt();

  const classes = [
    'card',
    faceDown ? 'card--back' : 'card--face',
    playable ? 'card--playable' : '',
    selected ? 'card--selected' : '',
    dimmed ? 'card--dimmed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (faceDown) {
    return (
      <div className={classes} style={style} aria-hidden="true">
        <div className="card__back-pattern" />
      </div>
    );
  }

  // Blank stock until the deck chunk lands. In practice it is loaded during the
  // lobby, long before a card is dealt.
  //
  // The markup injected below is a build-time asset generated by
  // tools/build-deck.mjs — it never comes from a player or the server.
  const art = deck?.[cardArtKey(card)];

  return (
    <div className={classes} style={style} role="img" aria-label={cardName(card)}>
      {art && <div className="card__art" dangerouslySetInnerHTML={{ __html: art }} />}
      {children}
    </div>
  );
}

/** The face-down back, used for opponents' hands and the draw pile. */
export function CardBack({ style, className = '' }: { style?: CSSProperties; className?: string }) {
  return (
    <div className={`card card--back ${className}`} style={style} aria-hidden="true">
      <div className="card__back-pattern" />
    </div>
  );
}
